import React, { useEffect, useRef, useState } from 'react';
import logo_img from './images/logo.png';
import 'font-awesome/css/font-awesome.min.css';
import './Navbar.css';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import {
  getDashboardPath,
  getUserDisplayName,
  getUserInitial,
  isBlogEditor,
  isLoggedIn,
  logout,
} from '../utils/authClient';
import NavMegaMenu from '../Components/NavMegaMenu';
import { useLocale } from '../context/LocaleContext';
import { useBookAppointmentModal } from '../context/BookAppointmentModalContext';

const Navbar = () => {
  const { t } = useLocale();
  const { openBookAppointmentModal } = useBookAppointmentModal();
  const [isActive, setActive] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const showBlogAdmin = isBlogEditor();
  const loggedIn = isLoggedIn();
  const displayName = loggedIn ? getUserDisplayName() : '';
  const navigate = useNavigate();
  const location = useLocation();
  const userMenuRef = useRef(null);

  const handleToggleMenu = () => {
    setActive((prev) => !prev);
  };

  const closeMobileMenu = () => {
    setActive(false);
  };

  const closeUserMenu = () => setUserMenuOpen(false);

  const goDashboard = () => {
    closeUserMenu();
    closeMobileMenu();
    navigate(getDashboardPath());
  };

  const doLogout = () => {
    closeUserMenu();
    logout();
    navigate('/', { replace: true });
  };

  useEffect(() => {
    setActive(false);
    setUserMenuOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!userMenuOpen) return;
    const onDocDown = (e) => {
      const el = userMenuRef.current;
      if (!el) return;
      if (el.contains(e.target)) return;
      setUserMenuOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [userMenuOpen]);

  useEffect(() => {
    if (!isActive) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setActive(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isActive]);

  return (
    <>
      <div className="nav-container">
        <div className="nav-inner">
          <div className="nav_brand">
            <div className="logo">
              <HashLink to="/#home" smooth onClick={closeMobileMenu}>
                <img src={logo_img} alt="Smile Up logo" />
              </HashLink>
            </div>
          </div>
          <div className="links links--desktop">
            <div className="links_panel">
              <NavMegaMenu onNavigate={closeMobileMenu} />
            </div>
          </div>

          <button
            type="button"
            className={`nav_reservas_cta nav_reservas_cta--bar nav_reservas_cta--desktop${
              location.pathname === '/reservas' ? ' nav_reservas_cta--active' : ''
            }`}
            aria-current={location.pathname === '/reservas' ? 'page' : undefined}
            onClick={() => {
              closeMobileMenu();
              openBookAppointmentModal();
            }}
          >
            <i className="fa fa-calendar" aria-hidden="true" />
            <span className="nav_reservas_cta_text">{t('stickyBook')}</span>
          </button>

          <div className="nav_right nav_right--desktop">
            {loggedIn ? (
              <div className="nav_user_menu" ref={userMenuRef}>
                <button
                  type="button"
                  className="nav_user_btn"
                  aria-haspopup="menu"
                  aria-expanded={userMenuOpen ? 'true' : 'false'}
                  aria-label={displayName ? `${t('nav.account')}: ${displayName}` : t('nav.account')}
                  title={displayName}
                  onClick={() => setUserMenuOpen((v) => !v)}
                >
                  <span className="nav_user_avatar" aria-hidden="true">
                    {getUserInitial()}
                  </span>
                </button>

                {userMenuOpen ? (
                  <div className="nav_user_dropdown" role="menu">
                    <button type="button" className="nav_user_item" role="menuitem" onClick={goDashboard}>
                      <i className="fa fa-calendar-check" aria-hidden="true" />
                      {t('nav.reservationsPanel')}
                    </button>
                    {showBlogAdmin ? (
                      <button
                        type="button"
                        className="nav_user_item"
                        role="menuitem"
                        onClick={() => {
                          closeUserMenu();
                          navigate('/admin/blog');
                        }}
                      >
                        <i className="fa fa-pencil" aria-hidden="true" />
                        {t('nav.adminBlog')}
                      </button>
                    ) : null}
                    <div className="nav_user_divider" role="separator" />
                    <button type="button" className="nav_user_item nav_user_item--danger" role="menuitem" onClick={doLogout}>
                      <i className="fa fa-sign-out" aria-hidden="true" />
                      {t('nav.logout')}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          <button
            type="button"
            className={`toggle_menu_icons${isActive ? ' is-open' : ''}`}
            onClick={handleToggleMenu}
            aria-expanded={isActive ? 'true' : 'false'}
            aria-controls="nav-mobile-menu"
            aria-label={isActive ? t('nav.closeMenu') : t('nav.openMenu')}
          >
            <span className="toggle_menu_icon" aria-hidden="true">
              <span className="toggle_menu_line" />
              <span className="toggle_menu_line" />
              <span className="toggle_menu_line" />
            </span>
          </button>
        </div>

        <div
          className={`nav_mobile_menu${isActive ? ' is-open' : ''}`}
          id="nav-mobile-menu"
          aria-hidden={isActive ? 'false' : 'true'}
        >
            <div className="nav_mobile_panel">
              <NavMegaMenu onNavigate={closeMobileMenu} variant="mobile" />
              <ul className="nav_mobile_extras">
                {loggedIn ? (
                  <>
                    <li>
                      <button type="button" className="nav_mobile_row" onClick={goDashboard}>
                        {t('nav.reservationsPanel')}
                        <i className="fa fa-chevron-right" aria-hidden="true" />
                      </button>
                    </li>
                    {showBlogAdmin ? (
                      <li>
                        <Link to="/admin/blog" className="nav_mobile_row" onClick={closeMobileMenu}>
                          {t('nav.adminBlog')}
                          <i className="fa fa-chevron-right" aria-hidden="true" />
                        </Link>
                      </li>
                    ) : null}
                    <li>
                      <button type="button" className="nav_mobile_row nav_mobile_row--muted" onClick={doLogout}>
                        {t('nav.logout')}
                        <i className="fa fa-chevron-right" aria-hidden="true" />
                      </button>
                    </li>
                  </>
                ) : (
                  <li>
                    <Link to="/login" className="nav_mobile_row" onClick={closeMobileMenu}>
                      {t('nav.login')}
                      <i className="fa fa-chevron-right" aria-hidden="true" />
                    </Link>
                  </li>
                )}
              </ul>
            </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
